import { useEffect, useRef, useState } from "react";
import { clampVoiceLevel } from "@/components/cursors/voice-gradient-style";

const VOLUME_SMOOTHING_RATE = 18;

export function useSmoothedVoiceVolume(volume: number, active: boolean) {
  const safeVolume = clampVoiceLevel(volume);
  const [smoothedVolume, setSmoothedVolume] = useState(safeVolume);
  const targetRef = useRef(safeVolume);
  const currentRef = useRef(safeVolume);

  useEffect(() => {
    targetRef.current = safeVolume;
    if (!active) {
      currentRef.current = safeVolume;
      setSmoothedVolume(safeVolume);
    }
  }, [active, safeVolume]);

  useEffect(() => {
    if (!active) return;
    let frame = 0;
    let lastTime = performance.now();

    const tick = (time: number) => {
      const dt = Math.min(0.05, (time - lastTime) / 1000);
      lastTime = time;
      const delta = targetRef.current - currentRef.current;
      const next =
        Math.abs(delta) < 0.001
          ? targetRef.current
          : currentRef.current +
            delta * (1 - Math.exp(-VOLUME_SMOOTHING_RATE * dt));

      if (next !== currentRef.current) {
        currentRef.current = next;
        setSmoothedVolume(next);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  return smoothedVolume;
}
